import type {
  AgentAnswer,
  AgentAnswers,
} from "@/lib/attributeReview/reviewRow.types";

import { Button } from "@/components/ui";
import { pathLabel } from "@/lib/attributeReview/pathLabel";

import { AgentAnswerCard, type AgentAnswerCardProps } from "./AgentAnswerCard";
import { CopyAnswerCard } from "./CopyAnswerCard";
import { StepNote } from "./StepNote";

type Props = Omit<AgentAnswerCardProps, "name" | "answer" | "usedTitle"> & {
  usedTitleOf: (path: string) => string | undefined;
  agent: AgentAnswers;
  fieldLabelOf: (id: string) => string;
  onCopy?: (copyOf: string) => void;
};

export function TwoAnswers({
  agent,
  fieldLabelOf,
  onCopy,
  usedTitleOf,
  ...rest
}: Props) {
  const { answers } = agent;
  const first = answers.find((answer) => answer.path)?.path ?? null;
  const copy = answers[0]?.copy;
  const both = first
    ? pathLabel(rest.labelOf, first)
    : copy && `Same as ${fieldLabelOf(copy)}`;
  if (agent.agreed && both) {
    if (first === rest.pickedPath) return null;
    return (
      <StepNote tone="warn" title={`Both AIs picked ${both}.`}>
        {first && rest.onUse && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => rest.onUse?.(first, answers[0]?.kind)}
          >
            Use {both}
          </Button>
        )}
      </StepNote>
    );
  }
  const card = (answer: AgentAnswer) =>
    answer.copy ? (
      <CopyAnswerCard
        key={answer.model}
        name={answer.model}
        copy={answer.copy}
        confidence={answer.confidence}
        fieldLabelOf={fieldLabelOf}
        onCopy={onCopy}
      />
    ) : (
      <AgentAnswerCard
        key={answer.model}
        name={answer.model}
        answer={answer}
        usedTitle={answer.path ? usedTitleOf(answer.path) : undefined}
        {...rest}
      />
    );
  return <div className="grid grid-cols-2 gap-2.5">{answers.map(card)}</div>;
}
